/**
 * UserRow – Single user row in the users table.
 * Displays checkbox, name, email, role, scope, and actions (edit/delete).
 * Memoized for list performance; depends only on callbacks (Dependency Inversion).
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Pencil, Trash2 } from 'lucide-react';
import ListCheckbox from '../common/ListCheckbox';
import {
  ACCESS_SCOPE_LABELS,
  getRoleDisplayName,
} from '../../constants/types/user.js';

const CELL_CLASS = 'px-6 py-4';
const TEXT_CLASS = "font-['Roboto:Regular',sans-serif] text-[15px] text-black";
const ACTION_BUTTON_EDIT =
  'text-gray-400 hover:text-gray-600 transition-colors';
const ACTION_BUTTON_DELETE = 'text-gray-400 hover:text-red-500 transition-colors';

export const UserRow = React.memo(function UserRow({
  user,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}) {
  const handleToggle = useCallback(() => onToggleSelect(user.id), [user.id, onToggleSelect]);
  const handleEdit = useCallback(() => onEdit(user), [user, onEdit]);
  const handleDelete = useCallback(() => onDelete(user), [user, onDelete]);

  const roleDisplay = getRoleDisplayName(user.accessScope);
  const scopeLabel = ACCESS_SCOPE_LABELS?.[user.accessScope]?.shortLabel ?? '';

  return (
    <tr className="bg-white border-b border-gray-200 hover:bg-gray-50 transition-colors">
      <td className={`${CELL_CLASS} w-[50px]`}>
        <ListCheckbox
          checked={isSelected}
          onToggle={handleToggle}
          ariaLabel={`Select ${user.fullName}`}
        />
      </td>
      <td className={CELL_CLASS}>
        <div className={TEXT_CLASS}>{user.fullName}</div>
      </td>
      <td className={CELL_CLASS}>
        <div className={TEXT_CLASS}>{user.email}</div>
      </td>
      <td className={CELL_CLASS}>
        <div className={TEXT_CLASS}>{roleDisplay}</div>
      </td>
      <td className={CELL_CLASS}>
        <div className={TEXT_CLASS}>{scopeLabel}</div>
      </td>
      <td className={`${CELL_CLASS} w-[120px]`}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleEdit}
            className={ACTION_BUTTON_EDIT}
            title="Edit user"
            aria-label="Edit user"
          >
            <Pencil className="size-5" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className={ACTION_BUTTON_DELETE}
            title="Delete user"
            aria-label="Delete user"
          >
            <Trash2 className="size-5" />
          </button>
        </div>
      </td>
    </tr>
  );
});

UserRow.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    email: PropTypes.string,
    accessScope: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
